import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import { alert } from 'actions/flash'
import {
  Handoff,
  RevenueCenterConfigRow as ConfigRow,
  useGetRevenueCenterConfigQuery,
  useGetRevenueCentersQuery,
  useRefreshRevenueCentersMutation,
} from 'api/revenueCenters'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import useDispatch from 'hooks/useDispatch'
import useFlag from 'hooks/useFlag'
import { buildTranslate } from 'locales'
import startCase from 'lodash/startCase'
import React, { useState } from 'react'
import Tab from 'react-bootstrap-five/Tab'
import { Redirect } from 'react-router-dom'
import CmsPage from 'scenes/Cms/components/CmsPage'
import { history } from 'store'
import OrderingNotEnabledPage from '../OrderingNotEnabledPage'
import DefaultTab from './DefaultTab'
import HandoffModesTab from './HandoffModesTab'

const t = buildTranslate('cms.settings.revenue_centers')

const TABS = {
  default: 'default',
  handoffModes: 'handoff_modes',
} as const

type Props = { showTitle?: boolean }

const RevenueCenters = ({ showTitle = true }: Props) => {
  const dispatch = useDispatch()
  const merchant = useCurrentMerchant()
  const revenueCentersFlag = useFlag('toast-revenue-center-enabled-mx', false)
  const canAccessRevenueCenters =
    revenueCentersFlag && Boolean(merchant?.supports_revenue_centers)

  const [activeTab, setActiveTab] = useState<string>(() => {
    const hash = history.location.hash.replace('#', '')
    return hash === TABS.handoffModes ? TABS.handoffModes : TABS.default
  })

  const {
    data: catalog,
    isLoading: catalogLoading,
    isError: catalogError,
  } = useGetRevenueCentersQuery(
    { includeStates: ['ACTIVE', 'ARCHIVED'] },
    { skip: !canAccessRevenueCenters }
  )
  const {
    data: configData,
    isLoading: configLoading,
    isError: configError,
  } = useGetRevenueCenterConfigQuery(undefined, {
    skip: !canAccessRevenueCenters,
  })
  const [refresh, { isLoading: isSyncing }] = useRefreshRevenueCentersMutation()

  // Defence-in-depth: the nav link is gated on the same flag + capability, but
  // the route is reachable directly. Send unsupported merchants home rather
  // than render an empty Revenue Center page.
  if (!canAccessRevenueCenters) {
    return <Redirect to="/" />
  }

  if (!merchant?.ordering_enabled) {
    return <OrderingNotEnabledPage />
  }

  const isLoading = catalogLoading || configLoading
  const hasError = catalogError || configError
  const revenueCenters = catalog?.revenue_centers ?? []
  const configs: ConfigRow[] = configData?.revenue_center_configs ?? []
  const defaultConfig = configs.find(c => (c.handoff ?? null) === null)
  const syncedAt = configData?.synced_at
  const supportedHandoffs: Handoff[] = configData?.supported_handoffs ?? []
  const providerName = startCase(merchant.ordering_provider ?? '')

  const selectTab = (tab: string | null) => {
    if (!tab) return
    history.replace({ ...history.location, hash: tab })
    setActiveTab(tab)
  }

  // Auto-sync runs server-side every 5 minutes; "Sync now" requests an
  // immediate background re-sync from the ordering provider. The catalog and
  // synced_at refresh once the job completes (RTK Query tag invalidation).
  const onSyncNow = async () => {
    try {
      await refresh().unwrap()
      dispatch(
        alert({
          key: 'success',
          message: t('sync.started', { provider: providerName }),
        })
      )
    } catch {
      dispatch(alert({ key: 'danger', message: t('sync.error') }))
    }
  }

  return (
    <CmsPage
      title={t('title')}
      description={t('description', { provider: providerName })}
      isLoading={isLoading}
      showTitle={showTitle}
    >
      {hasError ? (
        <div data-testid="rc-fetch-error" role="alert">
          <Notification kind="negative" size="full">
            <Text.BodyText4 color="grey70" className="p-xs" tag="div">
              {t('fetch_error')}
            </Text.BodyText4>
          </Notification>
        </div>
      ) : (
        <Tab.Container
          activeKey={activeTab}
          transition={false}
          onSelect={selectTab}
        >
          <TabNav ariaLabel={t('tablist_label')}>
            <TabItem state={TABS.default} title={t('tabs.default')} />
            <TabItem
              state={TABS.handoffModes}
              title={t('tabs.handoff_modes')}
            />
          </TabNav>
          <Tab.Content>
            <TabPane state={TABS.default}>
              <DefaultTab
                revenueCenters={revenueCenters}
                defaultConfig={defaultConfig}
                onSyncNow={onSyncNow}
                providerName={providerName}
                syncedAt={syncedAt}
                isSyncing={isSyncing}
              />
            </TabPane>
            <TabPane state={TABS.handoffModes}>
              <HandoffModesTab
                revenueCenters={revenueCenters}
                configs={configs}
                defaultName={defaultConfig?.revenue_center_name}
                onSyncNow={onSyncNow}
                providerName={providerName}
                syncedAt={syncedAt}
                isSyncing={isSyncing}
                supportedHandoffs={supportedHandoffs}
              />
            </TabPane>
          </Tab.Content>
        </Tab.Container>
      )}
    </CmsPage>
  )
}

export default RevenueCenters
